import { Link } from '@tanstack/react-router';
import { Check, Lock, Zap } from 'lucide-react';

import { AvatarCircles } from '@/components/ui/avatar-circles';
import { Button } from '@/components/ui/button';
import { Iphone } from '@/components/ui/iphone';
import { NumberTicker } from '@/components/ui/number-ticker';
import { landingContent } from '@/constants/landing-content';

const { hero } = landingContent;

const iconMap = {
  Check,
  Lock,
  Zap,
} as const;

const avatars = [
  {
    imageUrl: 'https://ui-avatars.com/api/?name=M&background=f472b6&color=fff',
    profileUrl: '#',
  },
  {
    imageUrl: 'https://ui-avatars.com/api/?name=P&background=a78bfa&color=fff',
    profileUrl: '#',
  },
  {
    imageUrl: 'https://ui-avatars.com/api/?name=J&background=60a5fa&color=fff',
    profileUrl: '#',
  },
  {
    imageUrl: 'https://ui-avatars.com/api/?name=C&background=34d399&color=fff',
    profileUrl: '#',
  },
];

export function Hero() {
  const scrollToHowItWorks = () => {
    const element = document.getElementById('how-it-works');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="container mx-auto max-w-6xl px-4 py-16">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        {/* Text Content */}
        <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
          {/* Social Proof */}
          <div className="mb-6 flex items-center gap-3">
            <AvatarCircles avatarUrls={avatars} />
            <p className="text-muted-foreground text-sm">
              <NumberTicker
                value={hero.socialProof.count}
                className="text-foreground font-semibold"
              />
              + {hero.socialProof.label}
            </p>
          </div>

          <h1 className="text-foreground text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
            {hero.headline}
          </h1>

          <p className="text-muted-foreground mt-6 max-w-2xl text-lg md:text-xl">
            {hero.subheadline}
          </p>

          <div className="mt-8 flex w-full flex-col gap-4 sm:flex-row md:w-auto">
            <Button asChild size="lg" className="text-base">
              <Link to="/registration">{hero.primaryCta}</Link>
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="text-base"
              onClick={scrollToHowItWorks}
            >
              {hero.secondaryCta}
            </Button>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-6">
            {hero.trustSignals.map((signal, index) => {
              const Icon = iconMap[signal.icon as keyof typeof iconMap];
              return (
                <div
                  key={index}
                  className="text-muted-foreground flex items-center gap-2 text-sm"
                >
                  <Icon className="text-primary h-4 w-4" />
                  <span>{signal.text}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* iPhone Mock */}
        <div className="flex justify-center lg:justify-end">
          <div className="relative">
            <Iphone className="h-125 w-auto md:h-150" src={hero.productImage} />
          </div>
        </div>
      </div>
    </section>
  );
}
